import { b64toBlob } from '~core';
import { changeDpiDataUrl } from 'changedpi';
import { hideSpellCheckForExport } from '~core/spellcheck';
import { modifyState } from '~src/state';

export const exportIMG = (
  quality,
  outputDpi,
  format = 'base64',
  imageFormat = 'jpeg',
  cleanBase64String = true,
  multiplier = 1,
  canvas
) => {
  if (!canvas) {
    canvas = window.canvas;
  }

  // Set flag to indicate we're exporting
  modifyState('isExporting', true);

  let restoreSpellCheck = () => {}; // No-op function by default
  let originalVisibility = [];

  // Only try to hide objects if canvas.getObjects is available
  if (canvas.getObjects && typeof canvas.getObjects === 'function') {
    // Hide spellcheck during export
    restoreSpellCheck = hideSpellCheckForExport(canvas);

    // Temporarily hide objects that should be excluded from visual export
    const objectsToHide = canvas
      .getObjects()
      .filter(
        (obj) =>
          obj.excludeFromExport ||
          obj.excludeFromVisualExport ||
          obj.isGuideline
      );

    // Store original visibility states
    originalVisibility = objectsToHide.map((obj) => ({
      object: obj,
      visible: obj.visible,
    }));

    // Hide objects that should be excluded
    objectsToHide.forEach((obj) => {
      obj.set('visible', false);
    });
  }

  let data;
  try {
    // Generate the export
    data = canvas.toDataURL({
      format: imageFormat,
      multiplier,
      quality,
      enableRetinaScaling: true,
      // filter option not available in current Fabric.js version
    });
  } finally {
    // Restore original visibility if we hid any objects
    if (originalVisibility.length > 0) {
      originalVisibility.forEach(({ object, visible }) => {
        object.set('visible', visible);
      });
    }

    try {
      // Restore spellcheck after export
      restoreSpellCheck();
    } finally {
      // Clear export flag
      modifyState('isExporting', false);
    }
  }

  if (!data || data === 'data:,') {
    const width = canvas.getWidth ? canvas.getWidth() : canvas.width;
    const height = canvas.getHeight ? canvas.getHeight() : canvas.height;
    throw new Error(
      `Unable to export canvas image. Canvas returned an empty data URL for ${width}x${height} at multiplier ${multiplier}.`
    );
  }

  if (outputDpi !== 72) {
    data = changeDpiDataUrl(data, parseInt(outputDpi));
  }

  const block = data.split(';');
  // Get the content type of the image
  const contentType = block[0].split(':')[1]; // In this case "image/gif"
  // get the real base64 content of the file
  const base64 = block[1].split(',')[1]; // In this case "R0lGODlhPQBEAPeoAJosM...."

  // Always return the blob regardless of format
  const blob = b64toBlob(base64, contentType);

  if (format === 'base64') {
    if (cleanBase64String) {
      return { base64, contentType, blob };
    } else {
      return { base64: data, contentType, blob };
    }
  }

  return { blob, contentType };
};
